import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseZap, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

/** Who may run a preview ⇄ production database mirror. Super admins only. */
export function DataSyncOperatorsCard() {
  const [operators, setOperators] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get("/data-sync/operators")
      .then((r) => { setOperators(r.data.operators || []); setCandidates(r.data.candidates || []); })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);

  if (denied) return null;

  const toggle = (email) => {
    const e = email.trim().toLowerCase();
    setOperators((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/data-sync/operators", { emails: operators });
      setOperators(data.operators || []);
      setDirty(false);
      toast.success("Data Sync operators updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Card className="space-y-4 border-slate-200 p-6 shadow-sm" data-testid="data-sync-operators-card">
      <div>
        <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-800">
          <DatabaseZap className="h-4 w-4 text-primary" /> Data Sync operators
        </h3>
        <p className="text-xs text-slate-400">
          Only these accounts can export or mirror a database (preview ⇄ production). Every other
          account — super admins included — is blocked from /data-sync. Changes are audit-logged.
        </p>
      </div>
      <div className="space-y-1">
        {candidates.length === 0 && <p className="text-xs text-slate-400">No active super admins found.</p>}
        {candidates.map((c) => {
          const email = (c.email || "").toLowerCase();
          const on = operators.includes(email);
          return (
            <button key={c.user_id} type="button" onClick={() => toggle(email)}
              data-testid={`data-sync-op-${email}`}
              className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors ${
                on ? "border-primary/40 bg-primary/5" : "border-slate-200 hover:bg-slate-50"}`}>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-slate-700">{c.name}</span>
                <span className="block truncate text-xs text-slate-400">{email}</span>
              </span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                on ? "bg-primary/15 text-primary" : "bg-slate-100 text-slate-400"}`}>
                {on ? "Operator" : "No access"}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <span className="text-xs text-slate-400">{operators.length} operator{operators.length === 1 ? "" : "s"}</span>
        <Button size="sm" onClick={save} disabled={!dirty || saving || operators.length === 0}
          data-testid="data-sync-operators-save" className="gap-1.5">
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save operators"}
        </Button>
      </div>
    </Card>
  );
}
